import { Model } from '@vuex-orm/core';
import moment from 'moment';
import { last, orderBy } from 'lodash-es';

import { Client } from './Client';
import { Volunteer } from './Volunteer';
import { CallAttempt } from './CallAttempt';
import { CallComment } from './CallComment';
import { CallAlert } from './CallAlert';
import { Period } from './Period';
import { serverDay, today } from '../datetime'

/**
 * Call Status
 */
export const STATUS = {
  NO_ATTEMPTS: 'no-attempts',
  RINGING: 'ringing',
  IN_PROGRESS: 'in-progress',
  FINISHED_SUCCESS: 'completed-success',
  FINISHED_FAILED: 'completed-failed'
};
export const STATUS_NAME = {
  [STATUS.NO_ATTEMPTS]: 'Geen pogingen',
  [STATUS.RINGING]: 'Bellen',
  [STATUS.IN_PROGRESS]: 'In behandeling',
  [STATUS.FINISHED_SUCCESS]: 'Gesproken',
  [STATUS.FINISHED_FAILED]: 'Niet bereikt'
};

/**
 * ORM Model
 * @see {@link https://vuex-orm.github.io/vuex-orm/guide/components/models.html}
 */
export class Call extends Model {
  static entity = 'calls'

  static primaryKey = 'id'

  static fields () {
    return {
      id: this.attr(null),
      status: this.string(null),
      volunteer_id: this.attr(null),
      client_id: this.attr(null),
      date: this.attr(null),
      period_id: this.attr(null),
      duration: this.number(null),
      call_attempts: this.attr(null),
      call_comment_id: this.attr(null),
      call_alert_id: this.attr(null),

      client: this.belongsTo(Client, 'client_id'),
      volunteer: this.belongsTo(Volunteer, 'volunteer_id'),
      callAttempts: this.hasMany(CallAttempt, 'call_id'),
      callComment: this.hasOne(CallComment, 'call_id'),
      callAlert: this.hasOne(CallAlert, 'call_id'),
      period: this.belongsTo(Period, 'period_id')
    };
  }

  static async load (id) {
    return await Call.$get({ params: { id }});
  }

  static history (query) {
    query = {
      limit: 5,
      before_date: moment().format('YYYY-MM-DD'),
      completed: true,
      order_by: 'date',
      order_direction: 'desc',
      ...query
    };
    return Call.$fetch({ query });
  }

  static async historyCount (params) {
    params = {
      before_date: moment().format('YYYY-MM-DD'),
      completed: true,
      ...params
    };
    return (await axios.get(`${this.methodConf.http.baseURL}/${this.methodConf.http.url}/count`, { params })).data.count;
  }

  /**
   * Does the call have any call attempts?
   */
  get has_call_attempts () {
    return this.callAttempts.length > 0;
  }

  /**
   * Get the last call attempt
   */
  get current_call_attempt () {
    if (!this.callAttempts) {
      return;
    }
    return last(orderBy(this.callAttempts, 'id'));
  }

  /**
   * Does the call have at least one successful attempt?
   */
  get has_finished_attempt () {
    return this.callAttempts.filter((ca) => ca.is_finished).length > 0;
  }

  /**
   * Does the call have at least one successful attempt?
   */
  get has_failed_attempt () {
    return this.callAttempts.filter((ca) => ca.is_failed).length > 0;
  }

  /**
   * Does the call have at least one successful attempt?
   */
  get has_active_attempt () {
    return this.callAttempts.filter((ca) => ca.is_active).length > 0;
  }

  /**
   * How many attempts does the call have?
   */
  get attempt_count () {
    return this.callAttempts.length;
  }

  /**
   * Is the call finished?
   *
   * Active means that the call appointment is finished.
   */
  get is_finished () {
    return this.is_successful || this.is_unsuccessful;
  }

  /**
   * Is the call finished and successful?
   *
   * Active means that the call appointment is finished and successful.
   */
  get is_successful () {
    return this.status === STATUS.FINISHED_SUCCESS;
  }

  /**
   * Is the call finished and has it failed?
   *
   * Active means that the call appointment is finished and has failed.
   */
  get is_unsuccessful () {
    return this.status === STATUS.FINISHED_FAILED;
  }

  /**
   * Information about the time of day.
   */
  get info_daypart () {
    return {
      name: this.period.label,
      timeslot: this.period.timeslot
    };
  }

  /**
   * Get the date in a readable form
   */
  get pretty_date () {
    return moment(this.date, 'YYYY-MM-DD').format('LL');
  }

  /**
   * Get the friendly name of the status
   */
  get statusName () {
    return STATUS_NAME[this.status];
  }

  get isInCurrentPeriod () {
    return serverDay(this.date) === today() &&
        this.period.isActive
  }

  /**
   * Lifecycle hook afterCreate
   * @param {*} model
  */
  static afterCreate (model) {
    if (!model.call_comment_id) {
      CallComment.insert({ data: { id: 'new_' + model.id, call_id: model.id, comment: '' }});
    }

    if (!model.call_alert_id) {
      CallAlert.insert({ data: { id: 'new_' + model.id, call_id: model.id, text: '' }});
    }
  }

  /**
   * Axios default overrides
   * @see {@link https://github.com/vuex-orm/plugin-axios/blob/master/README.md}
   */
  static methodConf = {
    http: {
      url: 'regicall/calls'
    },
    methods: {
      $update: {
        name: 'update',
        http: {
          url: '/:id',
          method: 'patch'
        }
      }
    }
  }
}
